/**
 * Visual editor for the consolidated CNPJ AEP report.
 *
 * Lets the user:
 *  - Reorder sections via native HTML5 drag-and-drop
 *  - Toggle inclusion of each section
 *  - Choose per-section page-break behaviour (before / after / none)
 *  - Toggle a "compact" mode that removes inter-section padding
 *  - Detect blank/half-empty pages before printing
 *  - Persist all of that per CNPJ via `/api/cnpj/{cnpj}/aep-editor-config`
 *  - Trigger window.print() with the saved configuration
 *
 * Reuses the EXACT same `AEPSections` components used by the final report,
 * via the shared `renderAEPSection(key, ctx)` helper — guaranteeing 1:1
 * fidelity between editor preview and the final PDF.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import api from "@/lib/api";
import {
  SECTIONS, ORIENT_CLASS, MARGIN_CLASS, FONT_CLASS, MARGIN_MM,
} from "@/pages/aep/shared";
import { deriveProbabilityIndex, deriveSeverityIndex, matrixClassification } from "@/lib/aepContent";
import { renderAEPSection } from "@/pages/aep/renderAEPSection";

const A4_PRINTABLE_HEIGHT_PX = 935; // 247mm × 96dpi/25.4 ≈ 935px (A4 minus 3cm top + 2cm bottom)

export default function CnpjAEPEditor() {
  const { cnpj } = useParams();
  const navigate = useNavigate();

  const [raw, setRaw] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [attachments, setAttachments] = useState({});
  const [config, setConfig] = useState(null);          // persisted editor config
  const [order, setOrder] = useState(SECTIONS.map((s) => s.k));
  const [included, setIncluded] = useState(() => Object.fromEntries(SECTIONS.map((s) => [s.k, s.k !== "assinaturas"])));
  const [customBreaks, setCustomBreaks] = useState({}); // { key: 'before'|'after'|'none' }
  const [compact, setCompact] = useState(false);
  const [matrizViews, setMatrizViews] = useState(["geral", "ghe", "individual"]);
  const [orient, setOrient] = useState("retrato");
  const [margin, setMargin] = useState("normal");
  const [font, setFont] = useState("normal");
  const [saving, setSaving] = useState(false);
  const [pageReport, setPageReport] = useState(null);  // { totalPages, suspectEmpty: [{key, fillPct}] }
  const previewRef = useRef(null);

  // ---- Load everything ---------------------------------------------------
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/cnpj/${cnpj}/aep`).then((r) => r.data),
      api.get(`/cnpj/${cnpj}/aep-overrides`).then((r) => r.data).catch(() => ({})),
      api.get(`/cnpj/${cnpj}/aep-attachments`).then((r) => r.data).catch(() => ({})),
      api.get(`/cnpj/${cnpj}/aep-editor-config`).then((r) => r.data).catch(() => ({})),
    ]).then(([rawData, ovs, atts, cfg]) => {
      if (!alive) return;
      setRaw(rawData);
      setOverrides(ovs || {});
      setAttachments(atts || {});
      setConfig(cfg || {});
      // Apply saved config (if present)
      if (Array.isArray(cfg?.section_order) && cfg.section_order.length) {
        const known = SECTIONS.map((s) => s.k);
        const filtered = cfg.section_order.filter((k) => known.includes(k));
        const missing = known.filter((k) => !filtered.includes(k));
        setOrder([...filtered, ...missing]);
      }
      if (cfg?.included && typeof cfg.included === "object") setIncluded((s) => ({ ...s, ...cfg.included }));
      if (cfg?.custom_breaks && typeof cfg.custom_breaks === "object") setCustomBreaks(cfg.custom_breaks);
      if (typeof cfg?.compact === "boolean") setCompact(cfg.compact);
      if (Array.isArray(cfg?.matriz_views)) setMatrizViews(cfg.matriz_views);
      if (cfg?.orient) setOrient(cfg.orient);
      if (cfg?.margin) setMargin(cfg.margin);
      if (cfg?.font) setFont(cfg.font);
    }).catch((e) => toast.error(e.response?.data?.detail || e.message));
    return () => { alive = false; };
  }, [cnpj]);

  // ---- Inject @page (same approach as CnpjAEPReport) --------------------
  useEffect(() => {
    const id_ = "aep-editor-page-style";
    let style = document.getElementById(id_);
    if (!style) { style = document.createElement("style"); style.id = id_; document.head.appendChild(style); }
    style.textContent = `@page { size: A4 ${orient}; margin: ${MARGIN_MM[margin]}; }`;
    return () => { if (style?.parentNode) style.parentNode.removeChild(style); };
  }, [orient, margin]);

  // ---- Adapt raw → AEPSection-friendly context ---------------------------
  const adapted = useMemo(() => {
    if (!raw) return null;
    const company = raw.canonical || {};
    const titlesList = (raw.assessments || []).map((x) => x.titulo).filter(Boolean);
    const ghes = Array.from(new Set((raw.assessments || []).map((x) => x.ghe_setor).filter(Boolean))).join(", ");
    const funcs = Array.from(new Set((raw.assessments || []).map((x) => x.funcao).filter(Boolean))).join(", ");
    const matrixType = raw.assessments?.[0]?.matrix_type || "pxs";

    const a = {
      id: `cnpj-${raw.cnpj}`,
      titulo: `AEP Consolidado — CNPJ ${raw.cnpj_formatted || raw.cnpj}`,
      ghe_setor: ghes || "Consolidado por CNPJ",
      funcao: funcs || "",
      n_trabalhadores: raw.expected_workers || 0,
      responsavel: raw.generated_by?.name || "",
      data_avaliacao: (raw.generated_at || "").slice(0, 10),
      observacoes: titlesList.length ? `Avaliações consolidadas (${titlesList.length}): ${titlesList.slice(0, 6).join("; ")}${titlesList.length > 6 ? "…" : ""}` : "",
      matrix_type: matrixType,
      company_snapshot: { ...company, cnpj: raw.cnpj_formatted || raw.cnpj },
    };
    const data = {
      assessment: a,
      dimensions: raw.dimensions || [],
      questions: raw.questions || [],
      overall_score: raw.overall_score || 0,
      overall: raw.overall || { level: 0, label: "Sem dados", key: "no_data" },
      response_count: raw.response_count || 0,
      critical_count: raw.critical_count || 0,
    };
    // Reclassifica overall usando a mesma lógica da Matriz AIHA para
    // manter Dashboard/Resumo e Matriz alinhados (fonte única de verdade).
    if ((data.response_count || 0) > 0 && data.dimensions.length > 0) {
      const worstScore = Math.max(...data.dimensions.filter((d) => d.responses > 0).map((d) => d.score), 0);
      const pIdx = deriveProbabilityIndex(data.overall_score);
      const sIdx = deriveSeverityIndex(worstScore, data.critical_count || 0, data.response_count, data.overall_score, Boolean(raw.assessments?.[0]?.aiha_strict));
      if (pIdx && sIdx) {
        const matrixLabel = matrixClassification(pIdx, sIdx);
        const levelMap = { "Baixo": 1, "Moderado": 2, "Alto": 3, "Crítico": 4 };
        const keyMap = { "Baixo": "low", "Moderado": "moderate", "Alto": "high", "Crítico": "critical" };
        data.overall = { ...data.overall, label: matrixLabel, level: levelMap[matrixLabel] || data.overall.level, key: keyMap[matrixLabel] || data.overall.key, matrix_derived: true };
      }
    }
    const actions = (raw.action_plans || []).map((p) => ({
      id: p.id, assessment_id: p.assessment_id, acao: p.acao,
      dimensao: p.dimensao || "", responsavel: p.responsavel || "",
      prazo: p.prazo || "", prioridade: p.prioridade || "Média",
      status: p.status || "Pendente",
    }));
    const responses = (raw.responses || []).map((r) => ({
      id: r.id, employee_name: r.employee_name || "",
      employee_matricula: r.employee_matricula || "",
      submitted_at: r.submitted_at || "", answers: r.answers || [],
    }));
    const date = new Date(raw.generated_at || Date.now()).toLocaleDateString("pt-BR");
    const hash = `AEP-CNPJ-${date.replaceAll("/", "")}-${(raw.cnpj || "").slice(-6).toUpperCase()}`;
    const score100 = Math.round((data.overall_score / 5) * 100);
    const criticalCount = data.critical_count || data.questions.filter((q) => q.score >= 4.5).length;
    const ranked = [...data.dimensions].filter((d) => d.responses > 0).sort((x, y) => y.score - x.score);
    const focoDim = ranked[0]?.dimensao || "";
    const critByDim = {};
    data.questions.forEach((q) => { if (q.score >= 4) critByDim[q.dimensao] = (critByDim[q.dimensao] || 0) + 1; });
    const maxResp = Math.max(...ranked.map((d) => d.responses), 1);
    const buckets = { "30 dias": 0, "60 dias": 0, "90 dias": 0, "180 dias": 0 };
    actions.forEach((act) => {
      if (!act.prazo) return;
      const diff = (new Date(act.prazo) - new Date()) / 86400000;
      if (diff <= 30) buckets["30 dias"] += 1;
      else if (diff <= 60) buckets["60 dias"] += 1;
      else if (diff <= 90) buckets["90 dias"] += 1;
      else if (diff <= 180) buckets["180 dias"] += 1;
    });
    return { a, company: a.company_snapshot, date, hash, score100, criticalCount, ranked, focoDim, critByDim, maxResp, data, actions, responses, timelineBuckets: buckets };
  }, [raw]);

  const renderCtx = adapted ? {
    ...adapted, included, cnpj: cnpj, overrides, onOverrideChange: () => {},
    onActionsChanged: () => {}, attachments, onAttachmentsChange: () => {}, raw, matrizViews,
  } : null;

  // ---- Drag & drop handlers ---------------------------------------------
  const dragKeyRef = useRef(null);
  const onDragStart = (k) => (e) => { dragKeyRef.current = k; e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (targetKey) => (e) => {
    e.preventDefault();
    const src = dragKeyRef.current;
    if (!src || src === targetKey) return;
    setOrder((ord) => {
      const next = ord.filter((k) => k !== src);
      const idx = next.indexOf(targetKey);
      next.splice(idx >= 0 ? idx : next.length, 0, src);
      return next;
    });
    dragKeyRef.current = null;
  };

  // ---- Persist config ----------------------------------------------------
  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put(`/cnpj/${cnpj}/aep-editor-config`, {
        section_order: order, included, custom_breaks: customBreaks, compact,
        matriz_views: matrizViews, orient, margin, font,
      });
      toast.success("Configuração salva.");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  // ---- Page detection ----------------------------------------------------
  // Measure each rendered section's offsetTop/offsetHeight inside the preview
  // container, then compute which page each section ends on. Pages where the
  // last section ends at <55% of the printable height are flagged.
  const detectPages = () => {
    const root = previewRef.current;
    if (!root) return;
    const sections = Array.from(root.querySelectorAll("[data-aep-section]"));
    if (!sections.length) { setPageReport({ totalPages: 0, suspectEmpty: [] }); return; }
    const rootTop = root.getBoundingClientRect().top + window.scrollY;
    const items = sections.map((el) => {
      const rect = el.getBoundingClientRect();
      const top = rect.top + window.scrollY - rootTop;
      const bottom = top + rect.height;
      return { key: el.getAttribute("data-aep-section"), top, bottom, height: rect.height };
    });
    const totalPages = Math.max(1, Math.ceil((items[items.length - 1].bottom) / A4_PRINTABLE_HEIGHT_PX));
    // Detect sections that leave > 45% empty space at the end of their page
    const suspectEmpty = [];
    items.forEach((it) => {
      const endPage = Math.floor(it.bottom / A4_PRINTABLE_HEIGHT_PX);
      const usedOnPage = it.bottom - (endPage * A4_PRINTABLE_HEIGHT_PX);
      const fillPct = Math.round((usedOnPage / A4_PRINTABLE_HEIGHT_PX) * 100);
      if (fillPct > 0 && fillPct < 55) suspectEmpty.push({ key: it.key, fillPct });
    });
    setPageReport({ totalPages, suspectEmpty });
  };

  // ---- Print -------------------------------------------------------------
  const triggerPrint = () => {
    setTimeout(() => window.print(), 50);
  };

  // Dynamic CSS for per-section break overrides + compact mode.
  // Generated from customBreaks so user changes reflect instantly in @print.
  // (declared BEFORE any early return so the hook order stays stable.)
  const dynamicCss = useMemo(() => {
    const rules = [];
    Object.entries(customBreaks).forEach(([k, v]) => {
      if (v === "none") {
        rules.push(
          `[data-aep-section="${k}"] > section,` +
          `[data-aep-section="${k}"] > section.page-break {` +
          `  page-break-before: auto !important; break-before: auto !important;` +
          `  page-break-after: auto !important;  break-after: auto !important;` +
          `}`,
        );
      } else if (v === "after") {
        rules.push(
          `[data-aep-section="${k}"] {` +
          `  page-break-after: always !important; break-after: page !important;` +
          `}`,
        );
      }
    });
    return rules.join("\n");
  }, [customBreaks]);

  if (!adapted) {
    return <div className="p-8 text-slate-500" data-testid="aep-editor-loading">Carregando…</div>;
  }

  const breakClassFor = (key) => {
    const v = customBreaks[key];
    if (v === "after") return "force-break-after";
    if (v === "none") return "no-break";
    // "before" or default: each AEP section already has page-break-before: always
    return "";
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="aep-editor-root">
      {/* Top toolbar — hidden in print */}
      <div className="no-print sticky top-0 z-30 bg-slate-900 text-white">
        <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
          <Link to={`/aep/cnpj/${cnpj}`} className="text-emerald-300 hover:text-emerald-200 text-sm" data-testid="aep-editor-back">
            <i className="fa-solid fa-arrow-left mr-1"></i> Voltar ao relatório
          </Link>
          <div className="text-sm font-bold tracking-wide">Editor de Páginas · {raw?.cnpj_formatted || cnpj}</div>
          <span className="text-xs text-slate-300 ml-2">{order.filter((k) => included[k]).length} de {order.length} seções</span>
          <div className="flex-1"></div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={compact} onChange={(e) => setCompact(e.target.checked)} data-testid="aep-editor-compact" />
            Compactar espaçamento
          </label>
          <select value={orient} onChange={(e) => setOrient(e.target.value)} className="bg-slate-800 text-white text-xs border border-slate-700 rounded px-2 py-1" data-testid="aep-editor-orient">
            <option value="retrato">Retrato</option>
            <option value="paisagem">Paisagem</option>
          </select>
          <select value={margin} onChange={(e) => setMargin(e.target.value)} className="bg-slate-800 text-white text-xs border border-slate-700 rounded px-2 py-1" data-testid="aep-editor-margin">
            <option value="estreita">Margem estreita</option>
            <option value="normal">Margem normal</option>
            <option value="larga">Margem larga</option>
          </select>
          <button onClick={detectPages} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold text-xs px-3 py-1.5 rounded" data-testid="aep-editor-detect">
            <i className="fa-solid fa-magnifying-glass mr-1"></i> Detectar páginas vazias
          </button>
          <button onClick={saveConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs px-3 py-1.5 rounded disabled:opacity-50" data-testid="aep-editor-save">
            <i className="fa-solid fa-floppy-disk mr-1"></i> {saving ? "Salvando…" : "Salvar configuração"}
          </button>
          <button onClick={triggerPrint} className="bg-white text-slate-900 font-bold text-xs px-3 py-1.5 rounded" data-testid="aep-editor-print">
            <i className="fa-solid fa-print mr-1"></i> Imprimir / PDF
          </button>
        </div>
        {pageReport && (
          <div className="px-4 py-2 bg-amber-50 text-amber-900 border-t border-amber-200 text-xs flex items-center gap-3 flex-wrap" data-testid="aep-editor-report">
            <span className="font-bold">📄 {pageReport.totalPages} página(s) estimada(s).</span>
            {pageReport.suspectEmpty.length === 0
              ? <span>✅ Nenhuma página com mais de 45% em branco detectada.</span>
              : <>
                  <span>⚠ {pageReport.suspectEmpty.length} seção(ões) deixam grande espaço vazio na página:</span>
                  {pageReport.suspectEmpty.map((s) => (
                    <span key={s.key} className="bg-amber-200 px-2 py-0.5 rounded">
                      {(SECTIONS.find((x) => x.k === s.key) || {}).label || s.key} ({s.fillPct}% preenchida)
                    </span>
                  ))}
                  <span className="opacity-70">→ tente arrastar essa seção para depois de outra mais curta, ou marcar &quot;sem quebra&quot;.</span>
                </>}
            <button onClick={() => setPageReport(null)} className="ml-auto text-amber-900 hover:underline">fechar</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 p-4 no-print">
        {/* Sidebar: orderable section list */}
        <aside className="bg-white rounded shadow border border-slate-200 p-3 max-h-[calc(100vh-100px)] overflow-y-auto" data-testid="aep-editor-sidebar">
          <h3 className="font-bold text-sm text-slate-900 mb-3">Ordem · Inclusão · Quebra</h3>
          <p className="text-[11px] text-slate-500 mb-3">Arraste pelo ícone <i className="fa-solid fa-grip-vertical"></i> para reordenar. Desmarque para excluir do PDF.</p>
          <ul className="space-y-1">
            {order.map((k) => {
              const sec = SECTIONS.find((s) => s.k === k);
              if (!sec) return null;
              const incl = !!included[k];
              const brk = customBreaks[k] || "default";
              return (
                <li
                  key={k}
                  draggable
                  onDragStart={onDragStart(k)}
                  onDragOver={onDragOver}
                  onDrop={onDrop(k)}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded border ${incl ? "bg-slate-50 border-slate-200" : "bg-slate-100 border-slate-200 opacity-50"} cursor-move`}
                  data-testid={`aep-editor-row-${k}`}
                >
                  <i className="fa-solid fa-grip-vertical text-slate-400 cursor-grab"></i>
                  <input
                    type="checkbox"
                    checked={incl}
                    onChange={(e) => setIncluded((s) => ({ ...s, [k]: e.target.checked }))}
                    data-testid={`aep-editor-incl-${k}`}
                  />
                  <span className="flex-1 text-xs text-slate-800 truncate" title={sec.label}>{sec.label}</span>
                  <select
                    value={brk}
                    onChange={(e) => setCustomBreaks((s) => {
                      const next = { ...s };
                      if (e.target.value === "default") delete next[k]; else next[k] = e.target.value;
                      return next;
                    })}
                    className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white"
                    title="Quebra de página"
                    data-testid={`aep-editor-brk-${k}`}
                  >
                    <option value="default">↵ padrão</option>
                    <option value="before">⤴ antes</option>
                    <option value="after">⤵ depois</option>
                    <option value="none">≡ sem quebra</option>
                  </select>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Live preview */}
        <main className="bg-white rounded shadow border border-slate-200 p-4 overflow-x-auto" data-testid="aep-editor-preview-wrap">
          <div className="text-[11px] text-slate-500 mb-2">Pré-visualização — a impressão usará exatamente este layout.</div>
          <div
            ref={previewRef}
            className={`mx-auto aep-body ${ORIENT_CLASS[orient]} ${MARGIN_CLASS[margin]} ${FONT_CLASS[font]} ${compact ? "aep-compact" : ""}`}
            lang="pt-BR"
            data-testid="aep-editor-preview"
          >
            {order.filter((k) => included[k]).map((k) => {
              const node = renderAEPSection(k, renderCtx);
              if (!node) return null;
              // Wrap each section so we can tag it for measurement + apply
              // per-section break class without touching the inner component.
              return (
                <div
                  key={k}
                  data-aep-section={k}
                  className={breakClassFor(k)}
                >
                  {node}
                </div>
              );
            })}
          </div>
        </main>
      </div>

      {/* In print mode the toolbar/sidebar is hidden; only the preview prints */}
      <style>{`
        @media print {
          .aep-compact section.mb-12 { margin-bottom: 0 !important; }
          [data-aep-section] { display: block; }
        }
        .aep-compact section.mb-12 { margin-bottom: 1rem !important; }
        ${dynamicCss}
      `}</style>
    </div>
  );
}
